import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import {
  fetchBlogFeed,
  formatPublishedAt,
  parseTags,
  readingTime,
} from "../data/blogApi";
import { fadeUp, easing } from "../utils/motionVariants";
import "../components/Section.css";
import "./BlogPage.css";

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", post: null, related: [], error: null });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading", post: null, related: [], error: null });
    fetchBlogFeed({ limit: 50, signal: controller.signal })
      .then((data) => {
        const post = data.posts.find((p) => p.slug === slug);
        if (!post) {
          setState({ status: "notfound", post: null, related: [], error: null });
          return;
        }
        const related = data.posts.filter((p) => p.slug !== slug).slice(0, 3);
        setState({ status: "ready", post, related, error: null });
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setState({ status: "error", post: null, related: [], error: err.message });
      });
    return () => controller.abort();
  }, [slug]);



  if (state.status === "loading") {
    return (
      <section className="section section-white">
        <Seo title="Loading article" path={`/blog/${slug}`} noindex />
        <p className="blog-status">Loading…</p>
      </section>
    );
  }

  if (state.status === "notfound") {
    return (
      <motion.section
        className="section section-white"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: easing }}
      >
        <Seo
          title="Article not found"
          path={`/blog/${slug}`}
          description="The article you're looking for couldn't be found. Browse all articles in the blog."
          noindex
        />
        <h2>Article not found</h2>
        <Link to="/blog" className="accent">← Back to Blog</Link>
      </motion.section>
    );
  }

  if (state.status === "error") {
    return (
      <section className="section section-white">
        <Seo title="Blog" path={`/blog/${slug}`} noindex />
        <div className="card blog-status blog-status-error">
          <p style={{ margin: 0 }}>Couldn't load this article right now.</p>
        </div>
        <p style={{ marginTop: 16 }}>
          <Link to="/blog" className="accent">← Back to Blog</Link>
        </p>
      </section>
    );
  }

  const { post, related } = state;
  const tags = parseTags(post.tags);
  const minutes = readingTime(post);
  const title = post.seoTitle || post.title;
  const description = post.metaDescription || post.excerpt;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description,
    datePublished: post.publishedAt,
    keywords: tags.join(", "),
    ...(post.featureImage && { image: post.featureImage }),
    author: {
      "@type": "Person",
      name: "Hirun Kolongahapitiya",
      url: "https://hirun.lk",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://hirun.lk/blog/${post.slug}`,
    },
  };

  return (
    <section className="section section-white">
      <Seo
        title={title}
        path={`/blog/${post.slug}`}
        description={description}
        image={post.featureImage}
        type="article"
        jsonLd={jsonLd}
      />
      <motion.div
        className="section-header"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <h2>{post.title}</h2>
        <div className="section-underline" />
      </motion.div>

      <motion.div
        className="blog-detail-meta"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.4, ease: easing }}
      >
        {post.category && <span className="blog-chip">{post.category}</span>}
        <time className="muted">{formatPublishedAt(post.publishedAt)}</time>
        {minutes && <span className="muted">· {minutes}</span>}
      </motion.div>

      {post.featureImage && (
        <motion.figure
          className="blog-detail-hero"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.08, duration: 0.5, ease: easing }}
        >
          <img src={post.featureImage} alt={post.title} />
          {post.imageCredit && (
            <figcaption className="blog-detail-credit">
              Photo by{" "}
              {post.imageCreditUrl ? (
                <a
                  href={post.imageCreditUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {post.imageCredit}
                </a>
              ) : (
                post.imageCredit
              )}
            </figcaption>
          )}
        </motion.figure>
      )}

      {post.excerpt && (
        <motion.p
          className="blog-detail-excerpt"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4, ease: easing }}
        >
          {post.excerpt}
        </motion.p>
      )}

      <motion.article
        className="card blog-content"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.45, ease: easing }}
        dangerouslySetInnerHTML={{ __html: post.content }}
      />

      {tags.length > 0 && (
        <div className="blog-tags" style={{ marginTop: 18 }}>
          {tags.map((tag) => (
            <span key={tag} className="blog-tag">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {related.length > 0 && (
        <motion.div
          style={{ marginTop: 36 }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.45, ease: easing }}
        >
          <h3 style={{ marginBottom: 12 }}>More articles</h3>
          <div className="blog-grid">
            {related.map((r) => (
              <article key={r.slug} className="blog-card">
                <Link to={`/blog/${r.slug}`} className="blog-card-link">
                  {r.featureImage && (
                    <div className="blog-card-cover">
                      <img src={r.featureImage} alt={r.title} loading="lazy" />
                    </div>
                  )}
                  <div className="blog-card-body">
                    <div className="blog-card-meta">
                      {r.category && <span className="blog-chip">{r.category}</span>}
                      <time className="muted">{formatPublishedAt(r.publishedAt)}</time>
                    </div>
                    <h3>{r.title}</h3>
                    {r.excerpt && <p className="muted">{r.excerpt}</p>}
                    <span className="blog-read-more accent">Read article →</span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </motion.div>
      )}

      <motion.div
        style={{ display: "flex", gap: 12, marginTop: 24 }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.4, ease: easing }}
      >
        <motion.button
          onClick={() => navigate(-1)}
          className="btn-back"
          whileHover={{ x: -4, scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
        >
          ← Back
        </motion.button>
        <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} style={{ display: "inline-block" }}>
          <Link to="/blog" className="btn-back">All articles</Link>
        </motion.div>
      </motion.div>
    </section>
  );
}
